import api from '../../../api';
import { ProcessMapSession } from './types';

const BASE = '/api/process-map-synthesis';

function modelId(): string | undefined {
  try {
    const saved = localStorage.getItem('selectedLLM');
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && parsed.id) return parsed.id;
    }
  } catch {
    /* keep default */
  }
  return undefined;
}

export async function createProcessMapSession(title?: string): Promise<ProcessMapSession> {
  const res = await api.post(`${BASE}/sessions`, { title });
  return res.data.session;
}

export async function listProcessMapSessions(): Promise<{ sessions: ProcessMapSession[]; totalCount: number }> {
  const res = await api.get(`${BASE}/sessions`);
  return { sessions: res.data.sessions || [], totalCount: res.data.totalCount || 0 };
}

export async function getProcessMapSession(id: number): Promise<ProcessMapSession> {
  const res = await api.get(`${BASE}/sessions/${id}`);
  return res.data.session;
}

export async function deleteProcessMapSession(id: number): Promise<void> {
  await api.delete(`${BASE}/sessions/${id}`);
}

/**
 * Submits every selected PDF in one batch request; the backend runs the
 * pipeline for all of them concurrently (worker-pool capped) and holds the
 * connection open (keepAlive) until the whole batch settles. Client timeout
 * is generous since a 5-10 document batch with 3 Claude calls each can run
 * several minutes.
 */
export async function submitProcessMapBatch(sessionId: number, files: File[]): Promise<{ session: ProcessMapSession; skipped: string[] }> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const mid = modelId();
  if (mid) form.append('modelId', mid);

  const res = await api.post(`${BASE}/sessions/${sessionId}/submit`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 900000,
  });
  if (!res.data?.success) throw new Error(res.data?.error || 'Submit failed');
  return { session: res.data.session, skipped: res.data.skipped || [] };
}

export async function stopProcessMapDocument(sessionId: number, docId: string): Promise<ProcessMapSession> {
  const res = await api.post(`${BASE}/sessions/${sessionId}/documents/${docId}/stop`);
  if (!res.data?.success) throw new Error(res.data?.error || 'Stop failed');
  return res.data.session;
}

export async function deleteProcessMapDocument(sessionId: number, docId: string): Promise<ProcessMapSession> {
  const res = await api.delete(`${BASE}/sessions/${sessionId}/documents/${docId}`);
  if (!res.data?.success) throw new Error(res.data?.error || 'Delete failed');
  return res.data.session;
}

export async function saveCorpusRule(instruction: string): Promise<{ id: string; instruction: string; patches: { op: string; role?: string; color?: string; afterHeading?: string; text?: string }[] }> {
  const res = await api.post(`${BASE}/corpus/rules`, { instruction });
  if (!res.data?.success) throw new Error(res.data?.error || 'The note did not become a rule.');
  return res.data.rule;
}

export async function applyCorpusRule(folder: string): Promise<{
  dateLabel: string;
  reports: { fileName: string; status: string; archivedAs?: string; commentsForReview: string[]; checks: { label: string; status: string }[]; results: { detail: string; status: string }[] }[];
}> {
  const res = await api.post(`${BASE}/corpus/apply`, { folder });
  if (!res.data?.success) throw new Error(res.data?.error || 'The folder update failed.');
  return { dateLabel: res.data.dateLabel, reports: res.data.reports || [] };
}

export async function overrideChecklistItem(sessionId: number, docId: string, index: number, status: 'passed' = 'passed'): Promise<ProcessMapSession> {
  const res = await api.patch(`${BASE}/sessions/${sessionId}/documents/${docId}/checklist`, { index, status });
  if (!res.data?.success) throw new Error(res.data?.error || 'Could not update checklist status.');
  return res.data.session;
}

export async function updateFinishedDocuments(sessionId: number, kind: 'format' | 'wording'): Promise<{
  session: ProcessMapSession;
  needsPerson: { id: string; fileName: string; what: string; use: string }[];
  updatedCount: number;
}> {
  const res = await api.post(`${BASE}/sessions/${sessionId}/update-finished`, { kind });
  if (!res.data?.success) throw new Error(res.data?.error || 'Update failed');
  return {
    session: res.data.session,
    needsPerson: res.data.needsPerson || [],
    updatedCount: res.data.updatedCount || 0,
  };
}

export type RevisionUploadItem = {
  file: File;
  sourceKind: 'redline' | 'comments' | 'other';
  extras?: File[];
};

export async function submitRevisionBatch(
  sessionId: number,
  items: RevisionUploadItem[],
): Promise<{ session: ProcessMapSession }> {
  const form = new FormData();
  items.forEach((item, index) => {
    form.append('files', item.file);
    form.append('kinds', item.sourceKind);
    (item.extras || []).forEach((extra) => {
      form.append('extras', extra);
      form.append('extraOwners', String(index));
    });
  });
  const mid = modelId();
  if (mid) form.append('modelId', mid);
  const res = await api.post(`${BASE}/sessions/${sessionId}/revisions/submit`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 900000,
  });
  if (!res.data?.success) throw new Error(res.data?.error || 'Submit failed');
  return { session: res.data.session };
}

export async function addRevisionChange(
  sessionId: number,
  docId: string,
  change: { original_snippet?: string; proposed_content?: string; reasoning?: string; change_type?: string; section_title?: string },
): Promise<ProcessMapSession> {
  const res = await api.post(`${BASE}/sessions/${sessionId}/documents/${docId}/revision-changes`, change);
  if (!res.data?.success) throw new Error(res.data?.error || 'Could not add that change.');
  return res.data.session;
}

export async function updateRevisionChanges(
  sessionId: number,
  docId: string,
  payload: { approvals?: Record<string, boolean>; edits?: Record<string, { proposed_content?: string; original_snippet?: string; reasoning?: string }> },
): Promise<ProcessMapSession> {
  const res = await api.patch(`${BASE}/sessions/${sessionId}/documents/${docId}/revision-changes`, payload);
  if (!res.data?.success) throw new Error(res.data?.error || 'Could not update those changes.');
  return res.data.session;
}

export async function applyRevisionDocument(sessionId: number, docId: string): Promise<ProcessMapSession> {
  const res = await api.post(`${BASE}/sessions/${sessionId}/documents/${docId}/revision-apply`);
  if (!res.data?.success) throw new Error(res.data?.error || 'Apply failed');
  return res.data.session;
}

export async function applyReadyRevisions(sessionId: number): Promise<ProcessMapSession> {
  const res = await api.post(`${BASE}/sessions/${sessionId}/revisions/apply`);
  if (!res.data?.success) throw new Error(res.data?.error || 'Apply failed');
  return res.data.session;
}

export async function downloadProcessMapDocument(sessionId: number, docId: string, fallbackName: string): Promise<void> {
  try {
    const res = await api.get(`${BASE}/sessions/${sessionId}/documents/${docId}/download`, {
      responseType: 'blob',
      timeout: 60000,
    });
    const blob = new Blob([res.data]);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const disposition = res.headers?.['content-disposition'] || '';
    const match = disposition.match(/filename="([^"]+)"/);
    a.download = match ? match[1] : fallbackName;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err: any) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error) throw new Error(parsed.error);
      } catch (inner: any) {
        if (!(inner instanceof SyntaxError)) throw inner;
      }
    }
    throw err;
  }
}
